import React, { useState, useRef } from 'react';
import { uploadImage, deleteImage } from '../../services/dataService';

const ImageUpload = ({
    currentImage,
    currentImagePath,
    onImageUpload,
    label,
    folder = 'images',
    aspectRatio = 1,
    className = ""
}) => {
    const [uploading, setUploading] = useState(false);
    const [dragOver, setDragOver] = useState(false);
    const fileInputRef = useRef(null);

    const handleFileSelect = async (file) => {
        if (!file || !file.type.startsWith('image/')) {
            alert('Please select a valid image file');
            return;
        }

        if (file.size > 25 * 1024 * 1024) {
            alert('File size must be less than 5MB');
            return;
        }

        setUploading(true);

        try {
            // Delete old image if it exists
            if (currentImagePath) {
                await deleteImage(currentImagePath);
            }

            // Upload new image
            const result = await uploadImage(file, folder);
            await onImageUpload(result.url, result.path);
        } catch (error) {
            console.error('Upload failed:', error);
            alert('Upload failed. Please try again.');
        } finally {
            setUploading(false);
        }
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setDragOver(false);
        const file = e.dataTransfer.files[0];
        handleFileSelect(file);
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        setDragOver(true);
    };

    const handleDragLeave = () => {
        setDragOver(false);
    };

    const handleDelete = async () => {
        if (window.confirm('Are you sure you want to delete this image?')) {
            try {
                if (currentImagePath) {
                    await deleteImage(currentImagePath);
                }
                await onImageUpload('', '');
            } catch (error) {
                console.error('Failed to delete image:', error);
                alert('Failed to delete image. Please try again.');
            }
        }
    };

    return (
        <div className={`space-y-4 ${className}`}>
            <div
                className={`relative border-2 border-dashed rounded-lg transition-colors ${dragOver
                    ? 'border-sage-400 bg-purple-50'
                    : 'border-gray-300 hover:border-sage-400'
                    }`}
                style={{ aspectRatio }}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
            >
                {currentImage ? (
                    <div className="relative w-full h-full">
                        <img
                            src={currentImage}
                            alt="Preview"
                            className="w-full h-full object-cover rounded-lg"
                        />
                        <div className="absolute inset-0 bg-black bg-opacity-0 hover:bg-opacity-50 transition-all duration-300 flex items-center justify-center rounded-lg">
                            <div className="opacity-0 hover:opacity-100 transition-opacity space-x-2">
                                <button
                                    onClick={() => fileInputRef.current?.click()}
                                    disabled={uploading}
                                    className="bg-white text-gray-800 px-3 py-2 rounded-lg disabled:opacity-50 text-sm"
                                >
                                    {uploading ? 'Uploading...' : 'Change'}
                                </button>
                                <button
                                    onClick={handleDelete}
                                    disabled={uploading}
                                    className="bg-red-600 text-white px-3 py-2 rounded-lg disabled:opacity-50 text-sm"
                                >
                                    Delete
                                </button>
                            </div>
                        </div>
                    </div>
                ) : (
                    <div
                        className="w-full h-full flex flex-col items-center justify-center cursor-pointer p-6"
                        onClick={() => fileInputRef.current?.click()}
                    >
                        {uploading ? (
                            <div className="text-center">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sage-600 mx-auto mb-2"></div>
                                <p className="text-gray-600">Uploading...</p>
                            </div>
                        ) : (
                            <div className="text-center">
                                <svg className="mx-auto h-12 w-12 text-gray-400 mb-4" stroke="currentColor" fill="none" viewBox="0 0 48 48">
                                    <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                                <p className="text-gray-600 mb-2">{label}</p>
                                <p className="text-sm text-gray-500">Click to upload or drag and drop</p>
                                <p className="text-xs text-gray-400 mt-1">PNG, JPG, WebP up to 5MB</p>
                            </div>
                        )}
                    </div>
                )}
            </div>

            <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={(e) => handleFileSelect(e.target.files[0])}
                className="hidden"
            />
        </div>
    );
};

export default ImageUpload;